/**
 * Jotai atom definitions for the BPA Findings feature.
 *
 * Design principles:
 *  - Storage atoms (atomWithStorage) persist user preferences across sessions.
 *  - Reset atoms (atomWithReset) can be snapped back to defaults.
 *  - Derived atoms (atom(get => …)) compute query params from primitive atoms
 *    so components always react to the minimal, correct dependency set.
 */

import { atom, useAtom, useSetAtom } from 'jotai';
import { atomWithReset, atomWithStorage, useResetAtom } from 'jotai/utils';
import { isValidDateValue } from 'views/bpaUrlFilterUtils';
import { companyIdAtom } from 'views/state';
import { DEFAULT_PAGE_SIZE } from 'views/types';

import {
  BPAFindingsInsightsData,
  BPAFindingsListingData,
  BPAFindingsOptionsData,
  BPAFindingsQueryParams,
  DateRangeFilter,
  FilterValues,
  PaginationConfig,
} from './types/bpaFindings.types';
import {
  BPA_FINDINGS_DATE_FILTER_KEYS,
  getDefaultBPAFindingsFilters,
} from './urlFilters';

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

/**
 * Raw filter values as entered/selected by the user.
 * Default: Scan Status = 'Active' so the grid shows only active
 * findings on first load.
 */
export const filterValuesAtom = atomWithReset<FilterValues>(
  getDefaultBPAFindingsFilters(),
);

/**
 * Framework/Practice type filter — persisted so the last-selected framework
 * survives a page refresh. 'all' means no framework filter is applied.
 */
export type FrameworkValueType = 'all' | number;
export const frameworkIdFilterAtom = atomWithStorage<FrameworkValueType>(
  'bpa_findings_framework_filter',
  'all',
);

/** Guard so the grid only fetches AFTER query-string filters are hydrated */
export const isQuerystringFiltersInitializedAtom = atom(false);

/** Date filter keys that need Date-object reconstruction from storage */
export const BpaDateFilterKeys: readonly string[] = [
  ...BPA_FINDINGS_DATE_FILTER_KEYS,
];

/**
 * Derived atom: rebuilds Date objects from serialised date-range values.
 * All other filter values pass through unchanged.
 */
export const parsedFilterValuesAtom = atom<FilterValues>((get) => {
  const filterValues = get(filterValuesAtom);
  const parsed: FilterValues = { ...filterValues };

  BpaDateFilterKeys.forEach((key) => {
    if (parsed[key]) {
      parsed[key] = (parsed[key] as DateRangeFilter[]).map((range) => ({
        from: range.from ? new Date(range.from) : null,
        to: range.to ? new Date(range.to) : null,
      }));
    }
  });

  return parsed;
});

// ---------------------------------------------------------------------------
// Pagination & sorting
// ---------------------------------------------------------------------------

export const paginationConfigAtom = atomWithStorage<PaginationConfig>(
  'bpa_findings_pagination_config',
  { page: 1, pageSize: DEFAULT_PAGE_SIZE },
);

export const orderByAtom = atomWithReset<string>('last_seen_at');

export const sortOrderAtom = atomWithReset<'asc' | 'desc'>('desc');

// ---------------------------------------------------------------------------
// Column visibility
// ---------------------------------------------------------------------------

export const DEFAULT_BPA_FINDINGS_COLUMN_KEYS = [
  'control_id',
  'description',
  'integration_integration_label',
  'benchmark_name',
  'control_service',
  'control_severity',
  'scan_status',
  'last_seen_at',
  'actions',
];

export const columnKeysBPAAtom = atomWithStorage<string[]>(
  'bpa_findings_columns',
  DEFAULT_BPA_FINDINGS_COLUMN_KEYS,
);

// ---------------------------------------------------------------------------
// Row selection
// ---------------------------------------------------------------------------

export const selectedRowIdsAtom = atomWithReset<number[]>([]);

// ---------------------------------------------------------------------------
// Refresh trigger
// ---------------------------------------------------------------------------

/** Changing this atom value forces the grid to re-fetch */
export const findingsDataRefreshAtom = atom<string>('');

// ---------------------------------------------------------------------------
// Data atoms
// ---------------------------------------------------------------------------

/** Holds the current page of finding records and loading/error state */
export const findingsDataAtom = atom<BPAFindingsListingData>({
  results: [],
  count: 0,
  loading: false,
});

/** Holds the server-computed insights from GET /bpa/findings/insights/ */
export const bpaInsightsAtom = atom<BPAFindingsInsightsData>({
  insights: null,
  loading: false,
});

/** Holds the dynamically fetched filter options (providers, modules, regions, benchmarks) */
export const findingsOptionsDataAtom = atom<BPAFindingsOptionsData>({
  providers: [],
  modules: [],
  regions: [],
  benchmarks: [],
  integrations: [],
});

// ---------------------------------------------------------------------------
// Derived query-param atoms
// ---------------------------------------------------------------------------

/**
 * Builds the full set of query parameters for GET /bpa/findings/.
 * This is the single source of truth consumed by the data-fetching hook.
 */
export const findingsDataQueryParamsAtom = atom<BPAFindingsQueryParams>(
  (get) => {
    const companyId = get(companyIdAtom);
    const pagination = get(paginationConfigAtom);
    const orderBy = get(orderByAtom);
    const sortOrder = get(sortOrderAtom);
    const filterValues = get(parsedFilterValuesAtom);
    const frameworkId = get(frameworkIdFilterAtom);
    // Reading this atom makes the grid react to manual refresh triggers
    void get(findingsDataRefreshAtom);

    const params: BPAFindingsQueryParams = {
      company_id: companyId,
      page: pagination.page,
      page_size: pagination.pageSize,
      ordering: sortOrder === 'desc' ? `-${orderBy}` : orderBy,
    };

    if (frameworkId !== 'all') {
      params['framework_id'] = frameworkId;
    }

    // Spread filter values into query params, converting arrays to
    // comma-joined strings (matching the existing IntegrationListing pattern).
    Object.entries(filterValues).forEach(([key, value]) => {
      if (!value) return;

      if (BpaDateFilterKeys.includes(key)) {
        // date-range filter: send as ISO strings
        const [range] = value as DateRangeFilter[];
        if (isValidDateValue(range?.from)) {
          params[`${key}_after`] = range.from.toISOString();
        }
        if (isValidDateValue(range?.to)) {
          params[`${key}_before`] = range.to.toISOString();
        }
      } else if (key === 'scan_status' && Array.isArray(value)) {
        // The Status filter mixes two backend concepts:
        //  - scan_status: 'Active' | 'Fixed'  (whether the check currently fails)
        //  - user_status: 'Unignored' | 'Ignored'  (whether the user muted it)
        // The grid renders the "IGNORED" label from `user_status`, so the
        // "Ignored" option must be routed to `user_status=Ignored` rather than
        // `scan_status=Ignored` (which the backend does not understand).
        const statuses = (value as string[]).filter((v) => v !== '');
        const scanStatuses = statuses.filter((v) => v !== 'Ignored');
        if (statuses.includes('Ignored')) {
          params['user_status'] = 'Ignored';
        }
        if (scanStatuses.length > 0) {
          params['scan_status'] = scanStatuses.join(',');
        }
      } else if (key === 'assignment_filters' && Array.isArray(value)) {
        (value as string[]).forEach((filter) => {
          const [paramKey, paramValue] = filter.split('=');
          if (paramKey && paramValue) {
            params[paramKey] = paramValue;
          }
        });
      } else if (Array.isArray(value) && value.length > 0) {
        // Cast to string[]: by this branch, date-range keys are already handled
        // above, so every remaining array value is a string array.
        // Filter out empty-string sentinel values (Badges "All" option) so they
        // are never sent to the API as `key=`.
        const joined = (value as string[]).filter((v) => v !== '').join(',');
        if (joined) params[key] = joined;
      } else if (typeof value === 'string' && value.length > 0) {
        params[key] = value;
      } else if (typeof value === 'boolean') {
        params[key] = value;
      }
    });

    return params;
  },
);

/**
 * Derived atom: builds query params for the insights endpoint.
 * Omits pagination and ordering params since insights are aggregated.
 */
export const insightsQueryParamsAtom = atom((get) => {
  const queryParams = get(findingsDataQueryParamsAtom);
  // Omit pagination and ordering params
  const {
    page: _page,
    page_size: _pageSize,
    ordering: _ordering,
    ...insightsParams
  } = queryParams;
  return insightsParams;
});

// ---------------------------------------------------------------------------
// Helper hooks
// ---------------------------------------------------------------------------

/**
 * Returns a callback that resets pagination to page 1 and clears selected
 * rows. Call this whenever the user changes a filter.
 */
export function useOnFilterChangeResetRelatedAtoms() {
  const [paginationConfig, setPaginationConfig] = useAtom(paginationConfigAtom);
  const resetSelectedRows = useResetAtom(selectedRowIdsAtom);

  return () => {
    resetSelectedRows();
    // Only write when the page actually needs to change; writing the same
    // value would still create a new object reference and trigger a second
    // recomputation of findingsDataQueryParamsAtom → second API call.
    if (paginationConfig.page !== 1) {
      setPaginationConfig({ ...paginationConfig, page: 1 });
    }
  };
}

/**
 * Returns a callback that resets ALL BPA Findings atoms to their
 * default values (used by the company-level reset).
 */
export function useResetAllBPAFindingsAtoms() {
  const resetFilterValues = useResetAtom(filterValuesAtom);
  const resetSelectedRows = useResetAtom(selectedRowIdsAtom);
  const resetOrderBy = useResetAtom(orderByAtom);
  const resetSortOrder = useResetAtom(sortOrderAtom);
  const setRefresh = useSetAtom(findingsDataRefreshAtom);
  const setFrameworkId = useSetAtom(frameworkIdFilterAtom);

  // paginationConfigAtom uses atomWithStorage (user preference), so reset
  // it by writing the default value rather than atomWithReset.
  const setPagination = useSetAtom(paginationConfigAtom);

  return () => {
    resetFilterValues();
    resetSelectedRows();
    resetOrderBy();
    resetSortOrder();
    setPagination({ page: 1, pageSize: DEFAULT_PAGE_SIZE });
    setRefresh(new Date().toISOString());
    setFrameworkId('all');
  };
}

/**
 * Returns a reset callback for company change.
 */
export function useOnCompanyChangeBPAFindingsResetAtoms() {
  const resetAll = useResetAllBPAFindingsAtoms();
  return resetAll;
}

// Re-export types so consumers don't need to dig into the types folder
export type { BPAFinding, BPAInsights } from './types/bpaFindings.types';
